import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Card,
  CardContent,
  CardActions,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  List as ListIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { playlistsAPI } from '../services/api';

// Компонент карточки плейлиста для мобильных устройств
const PlaylistCard = ({ playlist, onEdit, onDelete, onViewChannels }) => {
  return (
    <Card sx={{ 
      mb: 2, 
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Первая строка: основная информация */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          mb: 1,
          width: '100%'
        }}>
          {/* ID */}
          <Typography variant="body2" sx={{ 
            color: 'text.secondary', 
            fontSize: '0.8rem', 
            fontWeight: 600,
            minWidth: '24px',
            textAlign: 'center'
          }}>
            #{playlist.id}
          </Typography>

          {/* Название */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" sx={{ 
              fontSize: '0.9rem', 
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mb: 0.25
            }}>
              {playlist.name}
            </Typography>
            <Typography variant="caption" sx={{ 
              color: 'text.secondary',
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}>
              {playlist.description || 'Нет описания'}
            </Typography>
          </Box>

          {/* Статус */}
          {playlist.is_default ? (
            <Chip label="По умолчанию" color="primary" size="small" sx={{ fontSize: '0.7rem' }} />
          ) : (
            <Chip label="Обычный" color="default" size="small" sx={{ fontSize: '0.7rem' }} />
          )}
        </Box>

        {/* Вторая строка: дата создания и управление */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          width: '100%',
          minHeight: '44px'
        }}>
          {/* Дата создания */}
          <Typography variant="caption" sx={{ 
            color: 'text.secondary',
            fontSize: '0.75rem'
          }}>
            Создан: {new Date(playlist.created_at).toLocaleDateString('ru-RU')}
          </Typography>

          {/* Кнопки управления (touch-friendly) */}
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <IconButton
              size="medium"
              onClick={() => onViewChannels(playlist.id)}
              title="Просмотр каналов"
              sx={{ 
                minWidth: '44px', 
                minHeight: '44px',
                backgroundColor: 'info.light',
                color: 'info.contrastText',
                '&:hover': { backgroundColor: 'info.main' }
              }}
            >
              <ListIcon sx={{ fontSize: '18px' }} />
            </IconButton>
            <IconButton
              size="medium"
              onClick={() => onEdit(playlist.id)}
              title="Редактировать"
              sx={{ 
                minWidth: '44px', 
                minHeight: '44px',
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
                '&:hover': { backgroundColor: 'primary.main' }
              }}
            >
              <EditIcon sx={{ fontSize: '18px' }} />
            </IconButton>
            {!playlist.is_default && (
              <IconButton
                size="medium"
                onClick={() => onDelete(playlist.id, playlist.name)}
                title="Удалить"
                sx={{ 
                  minWidth: '44px', 
                  minHeight: '44px',
                  backgroundColor: 'error.light',
                  color: 'error.contrastText',
                  '&:hover': { backgroundColor: 'error.main' }
                }}
              >
                <DeleteIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const PlaylistsPage = () => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, playlistId: null, playlistName: '' });
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    loadPlaylists();
  }, []);

  const loadPlaylists = async () => {
    try {
      setLoading(true);
      const response = await playlistsAPI.getAll();
      setPlaylists(response.data);
      setError('');
    } catch (error) {
      console.error('Ошибка загрузки плейлистов:', error);
      setError('Ошибка загрузки плейлистов');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (playlistId, playlistName) => {
    setDeleteDialog({ open: true, playlistId, playlistName });
  };

  const handleConfirmDelete = async () => {
    const { playlistId } = deleteDialog;
    if (!playlistId) return;

    try {
      await playlistsAPI.delete(playlistId);
      await loadPlaylists();
      setError('');
    } catch (error) {
      console.error('Ошибка удаления плейлиста:', error);
      setError('Ошибка удаления плейлиста');
    } finally {
      setDeleteDialog({ open: false, playlistId: null, playlistName: '' });
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialog({ open: false, playlistId: null, playlistName: '' });
  };

  const handleEdit = (playlistId) => {
    navigate(`/playlists/${playlistId}`);
  };

  const handleCreate = () => {
    navigate('/playlists/new');
  };

  const handleViewChannels = (playlistId) => {
    navigate(`/playlists/${playlistId}/channels`);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography>Загрузка плейлистов...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Управление плейлистами
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Создать плейлист
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Адаптивное отображение плейлистов: карточки на мобильных, таблица на десктопе */}
      {isMobile ? (
        /* МОБИЛЬНАЯ ВЕРСИЯ - КАРТОЧКИ */
        <Box sx={{ 
          width: '100%', 
          maxWidth: '100vw', 
          overflow: 'hidden'
        }}>
          {playlists.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Плейлисты не найдены
              </Typography>
            </Paper>
          ) : (
            playlists.map((playlist) => (
              <PlaylistCard
                key={playlist.id}
                playlist={playlist}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onViewChannels={handleViewChannels}
              />
            ))
          )}
        </Box>
      ) : (
        /* ДЕСКТОПНАЯ ВЕРСИЯ - ТАБЛИЦА */
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Название</TableCell>
                <TableCell>Описание</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Создан</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {playlists.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Плейлисты не найдены
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                playlists.map((playlist) => (
                  <TableRow key={playlist.id} hover>
                    <TableCell>{playlist.id}</TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {playlist.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {playlist.description || 'Нет описания'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {playlist.is_default ? (
                        <Chip label="По умолчанию" color="primary" size="small" />
                      ) : (
                        <Chip label="Обычный" color="default" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(playlist.created_at).toLocaleDateString('ru-RU')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleViewChannels(playlist.id)}
                        title="Просмотр каналов"
                      >
                        <ListIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(playlist.id)}
                        title="Редактировать"
                      >
                        <EditIcon />
                      </IconButton>
                      {!playlist.is_default && (
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(playlist.id, playlist.name)}
                          title="Удалить"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Диалог подтверждения удаления */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить плейлист "{deleteDialog.playlistName}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Это действие нельзя отменить.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Отмена</Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PlaylistsPage;
